import React, {
  useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Hint from '../../../components/hint/hint.component';
import Button from '../../../components/button/button.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import TextBlock from '../../../components/text-block/text-block.component';
import Loader from '../../../components/loader/loader.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import ControlButtonBlock from '../../app/common/control-button-block/control-button-block.component';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import AnalyzeIcon from '../../../icons/analyze-icon';
import DownloadIcon from '../../../icons/download-icon';
import FileService from '../../../services/file-service';
import FormService from '../../../services/form-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import {
  DEAL_FILE_EDIT, DEAL_FILE_GROUP_CREATE, DEAL_FILE_ACCESS_LEVEL_VIEW,
} from '../../../constants/entity/permissions';
import { FILE_EXTENSION_LIST_ANALYZABLE } from '../../../constants/system/file-extensions';

const DealFileModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MODAL_TITLE_DEFAULT: string = 'New File';
  const MODAL_TITLE_EDIT_PREFIX: string = 'Edit';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isModalEditMode,
    isRightDrawerOpen,
    isNewEntity,
    isDealListModal,
    isDealFileAnalyzed,
    dealId,
    dealFileData,
    dealFileGroupData,
    dealFileFile,
    accessLevelList,
    dealFileGroupList,
    saveFormDeps,
    onCreate,
    onCancel,
    onCloseModal,
    onFileChange,
    onFileDelete,
    onFileViewClick,
    onCloseNewGroupForm,
    onSwitchModalEditMode,
    onCancelForm,
    onSubmitForm,
    onSubmitCreateForm,
    onDealFileAnalyzeClick,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [isNewGroupVisible, setIsNewGroupVisible]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);

  const dealFileAccessLevelViewPermissions: string[] = useMemo((): string[] => [DEAL_FILE_ACCESS_LEVEL_VIEW], []);

  const modalTitle: string = useMemo((): string => {
    if (!isNewEntity && dealFileData && dealFileData.file && dealFileData.file.name) {
      return `${(isEditMode || isModalEditMode) ? `${MODAL_TITLE_EDIT_PREFIX} ` : ''}${dealFileData.file.name}`;
    }

    return MODAL_TITLE_DEFAULT;
  }, [dealFileData, isNewEntity, isModalEditMode, isEditMode]);

  const dealFileId: number = (dealFileData && dealFileData.id) || FormService.FORM_KEY_NEW;
  const dealFileGroupId: number = (dealFileGroupData && dealFileGroupData.id) || FormService.FORM_KEY_NEW;
  const formKeyDealFile: string = FormService.getIndexedFormFieldKey(dealFileId);
  const formKeyDealFileGroup: string = FormService.getIndexedFormFieldKey(dealFileGroupId);
  const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);
  const formPrefix: string = isDealListModal ? `dealListData[${formKeyDeal}].dealFile`
    : `dealData.dealFilesData[${formKeyDealFileGroup}].files[${formKeyDealFile}]`;
  const withEditModeSwitcher: boolean = !isNewEntity && !isNewDeal && !isEditMode;

  const menuItemDealFileGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: dealFileGroupList,
  }), [dealFileGroupList]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: accessLevelList,
  }), [accessLevelList]);

  const dealFileFileChange: Function = useCallback((file: File): void => {
    if (onFileChange) {
      onFileChange(file, dealFileGroupId, dealFileId);
    }
  }, [dealFileGroupId, dealFileId, dealFileFile, dealId]);

  const dealFileFileDelete: Function = useCallback((): void => {
    if (onFileDelete) {
      onFileDelete(dealFileGroupId, dealFileId);
    }
  }, [dealFileGroupId, dealFileId, dealFileFile]);

  const dealFileAnalyzeClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, file: File | null,
  ): void => {
    if (onDealFileAnalyzeClick && file) {
      onDealFileAnalyzeClick(file);
    }

    event.stopPropagation();
  }, [isDealFileAnalyzed]);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onCancel && !isEditMode) {
      onCancel();
    } else if (onCloseModal) {
      onCloseModal();
    }

    setIsNewGroupVisible(false);
  }, [isEditMode]);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (isNewEntity && !isNewDeal && onCreate) {
      onSubmitCreateForm((): void => {
        onCreate();
        setIsNewGroupVisible(false);
      });
    } else if (onCloseModal) {
      onCloseModal();
    }
  }, [isSaveDisabled, isNewEntity, isNewDeal, isEditMode, ...(saveFormDeps || [])]);

  const openNewGroupForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewGroupVisible(true);
  }, []);

  const closeNewGroupForm: MouseEventHandlerFunction = useCallback((): void => {
    setIsNewGroupVisible(false);

    if (onCloseNewGroupForm) {
      onCloseNewGroupForm(dealFileGroupId, dealFileId);
    }
  }, [dealFileGroupId, dealFileId]);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && (isNewEntity || isModalEditMode) && !isEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else {
      if (isModalEditMode && onCancel) {
        onCancel();
      }

      if (onCloseModal) {
        onCloseModal();
      }
    }
  }, [isSaveDisabled, isNewEntity, isModalEditMode, isEditMode]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  return (
    <Fragment>
      {
        (isNewEntity || !!dealFileData) && (
          <ModalLayout
            isOpen={isOpen}
            isModalEditMode={isModalEditMode}
            withEditModeSwitcher={withEditModeSwitcher}
            title={modalTitle}
            editPermission={DEAL_FILE_EDIT}
            onCloseModal={cancelChangesAndCloseModal}
            onCloseModalByClickAway={closeModalByClickAway}
            onSwitchModalEditMode={onSwitchModalEditMode}
          >
            <Form>
              <Card isContainer withNoPadding>
                <Table>
                  <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                    {
                      (((isEditMode || isNewEntity || isNewDeal || isModalEditMode) && !isNewGroupVisible)
                        || (!isEditMode && !isNewEntity && !isNewDeal && !isModalEditMode)) && (
                        <TableRow>
                          <TableCell>
                            Group
                            {
                              (isEditMode || isNewDeal || isNewEntity || isModalEditMode) && (
                                <RequiredMark />
                              )
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isNewEntity || isNewDeal || isModalEditMode) ? (
                                <FormFieldSelect
                                  isBordered
                                  withBlankElement
                                  isDisabled={(isNewEntity && !!dealFileGroupId)
                                    || (dealFileData && !dealFileData.file)}
                                  name={`${formPrefix}.groupId`}
                                  optionsTotal={dealFileGroupList && dealFileGroupList.length}
                                  newItemComponent={(
                                    !isNewGroupVisible && (
                                      <MenuItemAddNewItem
                                        label="New Group"
                                        createPermission={DEAL_FILE_GROUP_CREATE}
                                        onClick={openNewGroupForm}
                                      />
                                    )
                                  )}
                                >
                                  {menuItemDealFileGroupList}
                                </FormFieldSelect>
                              ) : dealFileGroupData && dealFileGroupData.name
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && isNewGroupVisible && (
                        <TableRow>
                          <TableCell>
                            Group (new)
                          </TableCell>
                          <TableCell isTextRight>
                            <ActionButton onClick={closeNewGroupForm}>
                              <CancelFilledIcon />
                            </ActionButton>
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && isNewGroupVisible && (
                        <TableRow>
                          <TableCell>
                            Group Name
                            <RequiredMark />
                          </TableCell>
                          <TableCell>
                            <FormFieldText isBordered name={`${formPrefix}.group.name`} />
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && (
                        <TableRow>
                          <TableCell>
                            Name
                            <RequiredMark />
                          </TableCell>
                          <TableCell>
                            <FormFieldText isBordered name={`${formPrefix}.name`} />
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      (isNewDeal || isNewEntity || (!isEditMode && !isModalEditMode
                        && !isNewDeal && !isNewEntity)) && (
                        <TableRow>
                          <TableCell>
                            File
                            {
                              (isNewDeal || isNewEntity) && (
                                <RequiredMark />
                              )
                            }
                            {
                              isNewDeal && !isDealFileAnalyzed && !!dealFileFile
                              && FileService.checkFileExtension(dealFileFile, FILE_EXTENSION_LIST_ANALYZABLE) && (
                                <Hint
                                  title="You can analyze file content in order to fill out empty fields in this deal"
                                />
                              )
                            }
                          </TableCell>
                          <TableCell>
                            {
                              isNewEntity || isNewDeal ? (
                                <FlexContainer>
                                  <FormFieldUpload
                                    isDroppable
                                    withAllFilesAllowed
                                    uploadFile={dealFileFile}
                                    name={`${formPrefix}.file`}
                                    buttonLabel="Upload New File"
                                    onFileChange={dealFileFileChange}
                                    onFileDelete={dealFileFileDelete}
                                  />
                                  <FlexContainer>
                                    {
                                      isNewDeal && !isDealFileAnalyzed && !!dealFileFile
                                      && FileService.checkFileExtension(dealFileFile, FILE_EXTENSION_LIST_ANALYZABLE)
                                      && (
                                        <Button
                                          isText
                                          isActive
                                          isDefault
                                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                            dealFileAnalyzeClick(event, dealFileFile);
                                          }}
                                        >
                                          <AnalyzeIcon />
                                          <TextBlock>
                                            Analyze
                                          </TextBlock>
                                        </Button>
                                      )
                                    }
                                  </FlexContainer>
                                </FlexContainer>
                              ) : (
                                dealFileData && dealFileData.file && (
                                  <FilteredLink
                                    isExternal
                                    isDownloadLink
                                    link={dealFileData.file.link}
                                    onMouseDown={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                      onFileViewClick(
                                        event,
                                        dealFileData.file,
                                        dealFileGroupData.name,
                                      );
                                    }}
                                  >
                                    <DownloadIcon />
                                    {' '}
                                    Download
                                  </FilteredLink>
                                )
                              )
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      (!isEditMode && !isNewDeal && !isNewEntity && !isModalEditMode && dealFileData.user) && (
                        <TableRow>
                          <TableCell>
                            Created By
                          </TableCell>
                          <TableCell>
                            {dealFileData.user.fullName}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      (isEditMode || isNewEntity || isNewDeal || isModalEditMode) && (
                        <CheckAccess permissions={dealFileAccessLevelViewPermissions}>
                          <TableRow>
                            <TableCell>
                              Access Limit
                            </TableCell>
                            <TableCell>
                              <FormFieldSelect
                                isBordered
                                withBlankElement
                                isDisabled={accessLevelList && !accessLevelList.length}
                                blankElementLabel="Everyone"
                                name={`${formPrefix}.accessLevel`}
                                optionsTotal={accessLevelList && accessLevelList.length}
                              >
                                {menuItemAccessLevelList}
                              </FormFieldSelect>
                            </TableCell>
                          </TableRow>
                        </CheckAccess>
                      )
                    }
                  </TableBody>
                </Table>
              </Card>
              {
                isModalEditMode ? (
                  <div>
                    {
                      isSaving && (
                        <Loader isInline isFloating isModal isSubmit />
                      )
                    }
                    <ControlButtonBlock
                      isModal
                      isModalEditMode={isModalEditMode}
                      isNewEntity={isNewEntity}
                      isSaveDisabled={isSaveDisabled}
                      withOffset={isRightDrawerOpen}
                      onCancelForm={onCancelForm}
                      onSubmitForm={onSubmitForm}
                    />
                  </div>
                ) : (
                  <ModalButtonBlock>
                    {
                      isNewEntity && !isNewDeal && (
                        <Button onClick={cancelChangesAndCloseModal}>
                          Cancel
                        </Button>
                      )
                    }
                    <Button
                      isDisabled={isSaving}
                      isActive={isNewEntity && !isNewDeal}
                      onClick={applyChangesAndCloseModal}
                    >
                      {isNewEntity && !isNewDeal ? 'Save' : 'Close'}
                    </Button>
                    {
                      isSaving && !isSmallScreen && (
                        <Loader isInline isModal isSmall />
                      )
                    }
                  </ModalButtonBlock>
                )
              }
            </Form>
            <LeaveConfirmModal
              isSubModal
              isOpen={isLeaveConfirmModalOpen}
              onConfirm={onLeaveConfirm}
              onCloseModal={closeLeaveConfirmModal}
            />
          </ModalLayout>
        )
      }
    </Fragment>
  );
});

export default DealFileModal;
